/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo, useState } from 'react';
import Divider from 'src/components/ui/Divider/Divider';
import { useTranslation } from 'react-i18next';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { createColumnHelper } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import { useOutsideClick, useEventListeners } from 'src/hooks/utils';
import {
  getInboundPackageDeliverySlip,
  InboundOrder,
  InboundPackage,
  PackageLines,
  useInboundOrderPackageQuery,
} from '../../../services/inbound';
import Icon from '../../../components/ui/Icon/Icon';
import { NotAnnouncedBadge } from '../NotAnnouncedBadge';
import { ExtraUnitsBadge } from '../ExtraUnitsBadge';
import Input from '../../../components/ui/Input/Input';
import Badge, { BadgeType } from '../../../components/ui/Badge/Badge';
import Button, { ButtonPositionProps, ButtonToneProps } from '../../../components/ui/Button/Button';
import { Status } from '../../../utils/general';
import { DataView, UsePagedQuery } from '../../../components/ui/TableOverview/DataView';
import { useDataViewController } from '../../../hooks/useDataViewController';
import { SearchModels } from '../../../components/ui/TableOverview/utils/SearchFieldRegistry';

export function ProductLineStatusBadge({ status }: { status: PackageLines['status'] }) {
  const badgeType: Record<string, BadgeType> = {
    draft: BadgeType.Neutral2,
    delivered: BadgeType.Successful,
    cancel: BadgeType.Error,
  };

  return (
    <Badge
      className="max-w-min normal-case"
      label={status?.label ?? ''}
      expanded
      colorOverride={status?.color ? status.color : ''}
      type={status?.color ? '' : badgeType[status?.key?.toLowerCase() ?? ''] ?? BadgeType.Neutral}
    />
  );
}

export interface InboundPackageOverlayProps {
  inboundOrder: InboundOrder;
  inboundPackage: InboundPackage;
  onClose: () => void;
}

export const InboundPackageOverlay: React.FC<InboundPackageOverlayProps> = ({
  inboundOrder,
  inboundPackage,
  onClose,
}) => {
  const { t } = useTranslation();
  const mainRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoadingState] = useState<boolean>(false);
  const singleInboundOrderPackageQuery = useInboundOrderPackageQuery({
    path: { inbound_order_id: inboundOrder?.id ?? 0, package_id: inboundPackage?.package_id ?? 0 },
  });

  const hiddenCols = useMemo(() => {
    const returnedPackage = singleInboundOrderPackageQuery.data;

    if (!returnedPackage) return [];

    const hasAnyLot = returnedPackage.package_lines?.some(pl => pl.lot?.id);

    return hasAnyLot ? [] : ['lot'];
  }, [singleInboundOrderPackageQuery.data]);

  const pagedPackageLinesQuery: UsePagedQuery<PackageLines> = () => {
    const returnedPackage = singleInboundOrderPackageQuery.data;
    if (!returnedPackage) {
      return { data: undefined, isLoading: singleInboundOrderPackageQuery.isLoading };
    }

    const lines = returnedPackage.package_lines ?? [];

    return {
      data: {
        items: lines,
        results: lines,
        total: lines.length,
        page: returnedPackage.page ?? 1,
        size: returnedPackage.limit ?? lines.length,
      },
      isLoading: singleInboundOrderPackageQuery.isLoading,
    };
  };

  const columnHelper = createColumnHelper<PackageLines>();

  const getInboundPackageLinesColumns = (t: any) => {
    return [
      columnHelper.accessor(row => row.product.name, {
        id: 'name',
        size: 200,
        meta: { label: t('general.name') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => {
          const row = info.row.original; // ← full row data
          const { product } = row;

          return (
            <div className="flex flex-col gap-y-4">
              <span className="typo-text-14--bold text-full">{product.name}</span>
              <div className="flex flex-row gap-4">
                <span className="typo-text-12 text-faded">
                  {product.article_number} | {product.barcode}
                </span>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.quantity, {
        id: 'quantity',
        size: 100,
        meta: { label: t('general.quantity') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => (
          <Input
            className="mt-0"
            placeholder={t('general.quantity')}
            defaultValue={info.getValue()}
            value={info.getValue()}
            disabled
          />
        ),
      }),
      columnHelper.accessor(row => row.status, {
        id: 'status',
        size: 100,
        meta: { label: t('general.status') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => {
          return (
            <div className="flex">
              <span className="typo-text-14 text-full capitalize">
                <ProductLineStatusBadge status={info.getValue()} />
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.lot?.lot_name, {
        id: 'lot',
        size: 100,
        enableHiding: true,
        meta: { label: t('inbound.lot') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info =>
          info.getValue() ? (
            <Button onClick={() => {}} disabled tone={ButtonToneProps.SECONDARY} title={info.getValue()} />
          ) : null,
      }),
    ];
  };

  const columns = useMemo(() => {
    if (!inboundOrder) return [];
    return getInboundPackageLinesColumns(t);
  }, [inboundOrder, t]);

  const controller = useDataViewController();

  useOutsideClick(mainRef, e => {
    const event = e as MouseEvent;
    if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
      onClose();
    }
  });

  useEventListeners([{ event: 'keydown' }], e => {
    const event = e as KeyboardEvent;
    if (event.key === 'Escape') {
      onClose();
    }
  });

  const packageTypeLabel = inboundPackage?.package_type
    ? t(`inbound.${inboundPackage.package_type}`, { defaultValue: inboundPackage.package_type })
    : '';

  return (
    <div
      ref={mainRef}
      data-overlay-handles-outside="true"
      data-overlay-handles-escape="true"
      data-overlay-type="inbound-package"
      style={{ height: '100%', overflow: 'auto' }}>
      <ContainerBody>
        {inboundPackage && (
          <>
            <div className="flex flex-col gap-y-8 pb-40">
              <div className="flex gap-x-16 items-center">
                <Icon name="package" width={48} height={48} className="" />
                <h2 className="text-full typo-title-36">{inboundPackage.name}</h2>
                <NotAnnouncedBadge announced={inboundPackage.announced} />
                <ExtraUnitsBadge extra={inboundPackage.extra_units} />
              </div>
            </div>
            <Divider className="py-24" />
            <div className="flex flex-col w-full gap-16">
              <span className="typo-text-14--bold text-faded">{t('general.description')}:</span>
              <div className="w-1/2">
                <Input
                  className="mt-0"
                  placeholder={packageTypeLabel || t('general.package')}
                  defaultValue={packageTypeLabel}
                  value={packageTypeLabel}
                  icon="package"
                  disabled
                />
              </div>
            </div>
            <div className="mt-40 pb-24">
              <DataView<PackageLines>
                controller={controller}
                modelKey={SearchModels.Return}
                displayColumnSelect={false}
                noResultsLabel={t('general.no_data')}
                applyContainer={false}
                headline={undefined}
                kpiModel={undefined}
                tabs={undefined}
                displayActionsColumn={false}
                showFilters={false}
                showSearch={false}
                filterModelId=""
                columns={columns}
                actions={[]}
                initialHiddenColumns={hiddenCols}
                useDataQuery={pagedPackageLinesQuery}
              />
            </div>
            {inboundOrder.inbound_state?.key !== Status.CANCEL && inboundOrder.inbound_state?.key !== Status.DRAFT && (
              <>
                <Divider className="pt-12" />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    disabled={loading}
                    title={loading ? t('inbound.download_loading') : t('inbound.download')}
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    iconPosition={ButtonPositionProps.AFTER}
                    onClick={async () => {
                      try {
                        setLoadingState(true);
                        if (!inboundOrder.id || !inboundPackage.package_id) {
                          toast.error(t('errors.incomplete_input'), { autoClose: 5000 });
                          return;
                        }
                        const fileContent = await getInboundPackageDeliverySlip(
                          inboundOrder.id,
                          inboundPackage.package_id,
                        );

                        if (!fileContent) {
                          toast.error(t('errors.download_delivery_slip_error'), { autoClose: 5000 });
                          return;
                        }

                        const fileName = `delivery_slip_${inboundPackage.name}.pdf`;
                        const url = window.URL.createObjectURL(fileContent);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', fileName);

                        document.body.appendChild(link);
                        link.click();
                        link.remove();

                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        toast.error(t('inbound.download_delivery_slip_error'), { autoClose: 5000 });
                      } finally {
                        setLoadingState(false);
                      }
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}
      </ContainerBody>
    </div>
  );
};

export default InboundPackageOverlay;
